/* eslint-disable jsx-a11y/no-noninteractive-element-interactions */
/* eslint-disable jsx-a11y/click-events-have-key-events */
/* eslint-disable jsx-a11y/no-static-element-interactions */
import React, { useState, useContext, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import cn from 'classnames';
import useSWR from 'swr';
import 'react-phone-number-input/style.css';
import PhoneInput from 'react-phone-number-input';
import { Pathname } from 'Routes';
import { AppContext } from 'Context';
import { dataFetcher, endpoints } from 'Api';
import { createRecaptchaVerifier, sendFirebaseOtp, mapFirebaseAuthError } from 'Api/firebaseAuthClient';
import { isMobile } from 'react-device-detect';
import { encryptedKeyForCredentials, GOOGLE_RECAPTCHA_KEY } from 'Commons';
import LightRays from 'Components/reactbits/LightRays';
import RotatingLoader from 'Components/RotatingLoader';
import { AuthPage } from '../components/page';
import { Snackbar } from '../../../../Components/index';
import theme from '../components/auth-theme.module.css';
import styles from './index.module.css';

// Inline strokes rather than an icon package: the rest of the redesigned auth
// screens carry their glyphs the same way, and these four are the whole set
// this page needs.
const PhoneCheckIcon = () => (
  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
    <rect x="5" y="2" width="14" height="20" rx="3" />
    <path d="M11 18.5h2" />
    <path d="M8.9 10.4l2 2 4-4" />
  </svg>
);

// Kept alongside the commented-out "Step 1 of 2" pill it belongs to.
// eslint-disable-next-line no-unused-vars
const SparkIcon = () => (
  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
    <path d="M12 3l1.9 4.9L19 9.8l-5.1 1.9L12 16.6l-1.9-4.9L5 9.8l5.1-1.9L12 3z" />
  </svg>
);

const ShieldIcon = () => (
  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.7" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
    <path d="M12 3l7 3v5.5c0 4.3-2.9 7.8-7 9.5-4.1-1.7-7-5.2-7-9.5V6l7-3z" />
    <path d="M9.2 12.1l1.9 1.9 3.7-3.8" />
  </svg>
);

const MessageIcon = () => (
  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.7" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
    <path d="M20 12a7.5 7.5 0 0 1-10.9 6.7L4 20l1.3-4.1A7.5 7.5 0 1 1 20 12z" />
    <path d="M9 11.8h6M9 14.6h3.5" />
  </svg>
);

const LockIcon = () => (
  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.7" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
    <rect x="4.5" y="10.5" width="15" height="10" rx="3" />
    <path d="M8.5 10.5V7.8a3.5 3.5 0 0 1 7 0v2.7" />
  </svg>
);

const ArrowIcon = () => (
  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
    <path d="M4.5 12h14" />
    <path d="M13 6.5l5.5 5.5L13 17.5" />
  </svg>
);

const ASSURANCES = [
  { key: 'encrypted', Icon: ShieldIcon, label: 'Encrypted' },
  { key: 'oneTime', Icon: MessageIcon, label: 'One-time code' },
  { key: 'private', Icon: LockIcon, label: 'Never shared' },
];

export const NewMobileVerification = () => {
  const navigate = useNavigate();
  const { setAppSnackbar, userDetails, setUserDetails, langText } = useContext(AppContext);
  const isDesktopApp = sessionStorage.getItem('isDesktopApp') === 'true';
  const mobileVerificationText = langText?.mobileVerification;
  const email = sessionStorage.getItem(encryptedKeyForCredentials.email);
  const [phoneNumber, setPhoneNumber] = useState();
  const [selectedReCAPTCHA, selectReCAPTCHA] = useState(false);
  const [recaptcha, setRecaptcha] = useState('');
  const reCaptchaHandler = () => {
    if (window.grecaptcha && window.grecaptcha.execute) {
      window.grecaptcha.execute(GOOGLE_RECAPTCHA_KEY, { action: 'submit' }).then((value) => {
        if (value) {
          setRecaptcha(value);
          selectReCAPTCHA(true);
        } else {
          selectReCAPTCHA(false);
        }
      });
    }
  };
  useEffect(() => {
    const loadScriptByURL = (id, url, callback) => {
      const isScriptExist = document.getElementById(id);
      if (!isScriptExist) {
        const script = document.createElement('script');
        script.type = 'text/javascript';
        script.src = url;
        script.id = id;
        script.onload = function onScriptLoad() {
          if (callback) callback();
        };
        document.body.appendChild(script);
      }
      if (isScriptExist && callback) callback();
    };
    loadScriptByURL('recaptcha-key', `https://www.google.com/recaptcha/api.js?render=${GOOGLE_RECAPTCHA_KEY}`, () => {
      if (window.grecaptcha && window.grecaptcha.ready) {
        window.grecaptcha.ready(() => {
          reCaptchaHandler();
        });
      }
    });
  }, []);

  // Firebase Phone Auth sends and checks the SMS code straight from the
  // browser, so this screen must mint the confirmation session the code-entry
  // screen confirms against — the old Twilio-backed sendOtpToMobile endpoint
  // left that session empty and verification could never complete.
  const [requestingCode, setRequestingCode] = useState(false);
  const otpRecaptchaVerifierRef = useRef(null);
  const getOtpRecaptchaVerifier = () => {
    if (!otpRecaptchaVerifierRef.current) {
      otpRecaptchaVerifierRef.current = createRecaptchaVerifier('firebase-recaptcha-container-mobile');
    }
    return otpRecaptchaVerifierRef.current;
  };
  const requestVerificationCode = async () => {
    setRequestingCode(true);
    try {
      await sendFirebaseOtp(phoneNumber, getOtpRecaptchaVerifier());
      sessionStorage.setItem('#fs656gfhllh@#$', true);
      sessionStorage.setItem(encryptedKeyForCredentials.mobile, phoneNumber);
      setAppSnackbar({ message: 'Code sent to your mobile number', isVisible: true });
      // `replace` so Back can't step off the verification flow onto this screen.
      navigate(Pathname.authentication.newverifyCode, { replace: true });
    } catch (error) {
      setAppSnackbar({ message: mapFirebaseAuthError(error), isVisible: true });
      otpRecaptchaVerifierRef.current = null;
      reCaptchaHandler();
    } finally {
      setRequestingCode(false);
    }
  };

  const defaultSnackbar = { isVisible: false, message: '', type: '' };
  const [snackbarData, setSnackbar] = useState(defaultSnackbar);
  const { isValidating: updatingMobile, mutate: updateMobile } = useSWR(
    [endpoints.updateMyProfile, phoneNumber, email],
    {
      fetcher: (url, inputMobile, inputEmail) => dataFetcher(url, { mobile: inputMobile, email: inputEmail }),
      onSuccess: ({ success, message, token }) => {
        if (success) {
          setUserDetails({ ...userDetails, mobile: phoneNumber });
          sessionStorage.setItem('mvt', token);
          requestVerificationCode();
        } else {
          setAppSnackbar({
            isVisible: true,
            message:
              message
              || 'Oops! Something went wrong while updating phone number',
          });
        }
      },
      onError: (error) => {
        const responseData = error?.response?.data;
        setSnackbar({ isVisible: true, message: responseData?.message });
      },
    }
  );

  const handleCancel = () => {
    if (isDesktopApp) {
      const isWindows = sessionStorage.getItem('isWindowsDesktop') === 'true';
      const callbackScheme = isWindows ? 'http://localhost:8080/callback' : 'projector-app://callback';
      window.location.href = `${callbackScheme}?error=cancelled`;
    } else {
      navigate(-1);
    }
  };

  useEffect(() => {
    setPhoneNumber(sessionStorage.getItem(encryptedKeyForCredentials.mobile));
  }, []);

  // Updating the profile and minting the Firebase session are one action to the
  // user, so a single overlay covers both rather than the button flickering
  // between them.
  const isBusy = updatingMobile || requestingCode;
  const canSubmit = Boolean(phoneNumber && phoneNumber.length) && !isBusy;

  return (
    <AuthPage className={theme.page} contentClassName={theme.content}>
      {/* Same backdrop as the redesigned /newSign-in and code screens. */}
      <div className={theme.rays} aria-hidden="true">
        <LightRays
          raysOrigin="top-center"
          raysColor="#ffffff"
          raysSpeed={0.9}
          lightSpread={0.85}
          rayLength={1.25}
          fadeDistance={1.1}
          saturation={1}
          followMouse
          mouseInfluence={0.08}
          noiseAmount={0.08}
          distortion={0.04}
        />
      </div>
      {isBusy && <RotatingLoader background="rgba(6, 6, 8, 0.72)" />}
      {!isMobile && !isDesktopApp && (
        <button type="button" className={cn(theme.back, styles.backBtn)} onClick={() => { navigate(-1); }}>
          Back
        </button>
      )}
      {isDesktopApp && (
        <button type="button" className={cn(theme.back, styles.backBtn)} onClick={handleCancel}>
          Cancel
        </button>
      )}

      <div className={theme.card}>
        {/* Step 1 of 2 — this screen, then the code screen.
        <div className={styles.rail} aria-hidden="true">
          <span className={styles.railFill} />
        </div> */}

        <form className={theme.stepIn} onSubmit={(e) => { e.preventDefault(); if (canSubmit) updateMobile(); }}>
          <div className={styles.hero}>
            <div className={styles.badge} aria-hidden="true">
              <PhoneCheckIcon />
            </div>
            {/* <span className={styles.eyebrow}>
              <SparkIcon />
              Step 1 of 2
            </span> */}
            <h1 className={theme.title}>
              {mobileVerificationText?.enterYourPhoneNumber || 'Verify your phone number'}
            </h1>
            <p className={theme.subtitle}>
              We&apos;ll text you a one-time code to confirm it&apos;s really you —
              it&apos;s how we keep your memories safe.
            </p>
          </div>

          <div className={styles.phoneField}>
            <div className={styles.fieldLabel}>
              <label htmlFor="mobile-verification-phone">Phone number</label>
            </div>
            <PhoneInput
              id="mobile-verification-phone"
              placeholder={
                mobileVerificationText?.enterYourPhoneNumber
                || 'Enter your phone number'
              }
              value={phoneNumber}
              onChange={setPhoneNumber}
              countryCallingCodeEditable={false}
              defaultCountry="US"
            />
          </div>

          <button
            type="submit"
            className={cn(theme.primary, styles.submitBtn)}
            disabled={!canSubmit}
          >
            {mobileVerificationText?.continue || 'Send verification code'}
            <ArrowIcon />
          </button>

          <div className={styles.assurance}>
            {ASSURANCES.map(({ key, Icon, label }) => (
              <div className={styles.assuranceItem} key={key}>
                <span className={styles.assuranceIcon}>
                  <Icon />
                </span>
                {label}
              </div>
            ))}
          </div>
        </form>
      </div>

      <p className={styles.fineprint}>
        <LockIcon />
        Your number is used only to verify this account and is never shared.
      </p>

      <div id="firebase-recaptcha-container-mobile" />
      <Snackbar
        {...snackbarData}
        onClose={() => {
          setSnackbar({ ...snackbarData, isVisible: false });
        }}
      />
    </AuthPage>
  );
};
